import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "reactstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import Loader from "pages/Separate/Loader";
import StepServices from "./Steps/StepServices";
import StepFeatures from "./Steps/StepFeatures";
import StepTiming from "./Steps/StepTiming";
import StepCosting from "./Steps/StepCosting";
import StepOverview from "./Steps/StepOverview";
import { fetchNodeData } from "utils/common-fetchData";
import secureLocalStorage from "react-secure-storage";
import axios from "axios";
import Swal from "sweetalert2";
import configure from "configure";
import "./index.css";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const userId = () => Number(localStorage.getItem("auth_id"));

const STEP_KEY = { 1: "step1", 2: "step2", 3: "step3", 4: "step4" };
const STEP_TITLE = {
  1: "Services",
  2: "Features",
  3: "Timing",
  4: "Costing",
  5: "Overview",
};
const STEP_DESC_DEFAULT = {
  1: "Select services",
  2: "Add project features",
  3: "Estimate hours",
  4: "Define cost",
  5: "Final review",
};

// Parse "1,1,1,0" → { view:1, edit:1, add:1, approve:0 }
const parseStepPerms = (str) => {
  const arr = (str || "0,0,0,0").split(",");
  return {
    view: Number(arr[0]),
    edit: Number(arr[1]),
    add: Number(arr[2]),
    approve: Number(arr[3]),
  };
};

// Parse "1,0" → { view:1, approve:0 }
const parseStep5Perms = (str) => {
  const arr = (str || "0,0").split(",");
  return {
    view: Number(arr[0]),
    approve: Number(arr[1]),
  };
};

const buildPerms = (row) => ({
  step1: parseStepPerms(row.step1),
  step2: parseStepPerms(row.step2),
  step3: parseStepPerms(row.step3),
  step4: parseStepPerms(row.step4),
  step5: parseStep5Perms(row.step5),
});

const DEFAULT_PERMS = {
  step1: { view: 0, edit: 0, add: 0, approve: 0 },
  step2: { view: 0, edit: 0, add: 0, approve: 0 },
  step3: { view: 0, edit: 0, add: 0, approve: 0 },
  step4: { view: 0, edit: 0, add: 0, approve: 0 },
  step5: { view: 0, approve: 0 },
};

// Compute role hours from timing object
const computeRoleHours = (timing, features, role) => {
  const k = (svcId, fi, si) => `${svcId}|${fi}|${si}|${role}`;
  return (features || []).reduce(
    (acc, svc) =>
      acc +
      svc.subtitles.reduce(
        (a2, sub, fi) =>
          a2 +
          Array.from(
            { length: sub.features.length },
            (_, si) => timing[k(svc.serviceId, fi, si)] || 0,
          ).reduce((x, y) => x + y, 0),
        0,
      ),
    0,
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const RequestForProposal = () => {
  const { id: leadId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(false);
  const [allOption, setAllOption] = useState({});
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    axios
      .get(`${configure.apiUrl}/staff`, {
        headers: { Accept: "application/json" },
      })
      .then((res) => {
        if (res.data.success) setAllUsers(res.data.data.filter(item => item?.login_access === 1) || []);
      })
      .catch(console.error);
  }, []);

  // ── RFP meta ───────────────────────────────────────────────────────────────
  const [rfpId, setRfpId] = useState(null);
  const [rfpStatus, setRfpStatus] = useState(null);
  const [stepStatus, setStepStatus] = useState({
    step1: "pending",
    step2: "pending",
    step3: "pending",
    step4: "pending",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [step, setStep] = useState(1);
  const [stepLogs, setStepLogs] = useState({
    step1: { saved: [] },
    step2: { saved: [], submitted: [], approved: [] },
    step3: { saved: [], submitted: [], approved: [] },
    step4: { saved: [] },
    step5: { approved: [] },
  });

  const [isUnlocked, setIsUnlocked] = useState(false);
  const isFinallyApproved = rfpStatus === "approved";
  const isApproved = isFinallyApproved && !isUnlocked;

  // ── Permissions ────────────────────────────────────────────────────────────
  const [myPerms, setMyPerms] = useState(DEFAULT_PERMS);

  // ── Wizard data ────────────────────────────────────────────────────────────
  const [services, setServices] = useState([]);
  const [pricingMode, setPricingMode] = useState("hourly");
  const [features, setFeatures] = useState([]);
  const [timing, setTiming] = useState({});
  const [extraHours, setExtraHours] = useState({});

  // Step 4 — lifted for Overview
  //------------------remove thsese
  const [costing, setCosting] = useState([]);
  const [devTechOption, setDevTechOption] = useState(null);
  const [baTechOption, setBaTechOption] = useState(null);
  const [qaTechOption, setQaTechOption] = useState(null);
  const [costingCurrency, setCostingCurrency] = useState(null);
  const [profitPct, setProfitPct] = useState("");
  //-----------------------------------------

  // Step 4 — lifted costing state
  const [techOption, setTechOption] = useState(null);
  const [overheadTotal, setOverheadTotal] = useState(0);
  const [commissionStaff, setCommissionStaff] = useState(null);
  const [commissionPct, setCommissionPct] = useState("");
  const [goCardlessEnabled, setGoCardlessEnabled] = useState(false);
  const [goCardlessPct, setGoCardlessPct] = useState("");
  // const [vatPct, setVatPct] = useState("20");
  const [gstPct, setGstPct] = useState("18");
  const [savedCosting, setSavedCosting] = useState(null);


  document.title = "Request For Proposal | Zithas Technologies";

  const token = secureLocalStorage.getItem("auth_token");

  // ── Derived permission helpers ─────────────────────────────────────────────
  // shorthand: p(1).view, p(1).add, p(1).approve etc.
  const p = (stepNum) =>
    myPerms[`step${stepNum}`] || { view: 0, edit: 0, add: 0, approve: 0 };

  // Steps this user can see in sidebar
  const visibleSteps = [1, 2, 3, 4, 5].filter((s) => {
    if (s === 5) return myPerms.step5.view == 1;
    return p(s).view == 1;
  });

  // const isReadOnly = (stepNum) => p(stepNum).add == 0 && p(stepNum).edit == 0;
  // const canSave = (stepNum) => p(stepNum).add == 1 || p(stepNum).edit == 1;

  // Can the user write to this step RIGHT NOW given status + permissions + edit mode?
  const canWriteToStep = (stepNum) => {
    const status = getStepStatus(stepNum);

    // After unlock (post final-approval reopening) — edit perm is enough
    if (isUnlocked) return p(stepNum).edit == 1 || p(stepNum).add == 1;

    // Edit mode active (clicked the Edit button on a done/submitted step) — needs edit perm
    if (isEditMode) return p(stepNum).edit == 1;

    // Step already saved/submitted/approved without edit mode — locked
    if (status === "done" || status === "submitted" || status === "approved") return false;

    // Step is pending (first-time fill-in) — needs ADD permission
    return p(stepNum).add == 1;
  };

  // Keep these aliases so the rest of the code keeps working
  const isReadOnly = (stepNum) => !canWriteToStep(stepNum);
  const canSave = (stepNum) => canWriteToStep(stepNum);

  const canSubmit = (stepNum) => p(stepNum).add == 1;
  const canApprove = (stepNum) => {
    if (stepNum === 5) return myPerms.step5.approve == 1;
    return p(stepNum).approve == 1;
  };

  // Navigation among visible steps
  const currentIdx = visibleSteps.indexOf(step);
  const isFirstStep = currentIdx === 0;
  const isLastStep = currentIdx === visibleSteps.length - 1;

  const [searchParams] = useSearchParams();

  const canProceedFromStep = (stepNum) => {
    if (stepNum === 1) return getStepStatus(1) === "done";
    if (stepNum === 2) return getStepStatus(2) === "approved";
    if (stepNum === 3) return getStepStatus(3) === "approved";
    if (stepNum === 4) return getStepStatus(4) === "done";
    return true;
  };

  const nextStep = () => {
    if (!isLastStep) {
      if (!canProceedFromStep(step)) {
        const requirements = {
          1: "Please save Step 1 before continuing.",
          2: "Step 2 must be approved before continuing.",
          3: "Step 3 must be approved before continuing.",
          4: "Please save Step 4 before continuing.",
        };
        Swal.fire({
          icon: "warning",
          text: requirements[step],
          confirmButtonColor: "#752a97",
        });
        return;
      }
      setStep(visibleSteps[currentIdx + 1]);
      setIsEditMode(false);
    }
  };

  const canNavigateToStep = (targetStep) => {
    // Can always go back or stay
    if (targetStep <= step) return true;

    // To go forward, every step before target must meet its requirement
    for (let s of visibleSteps) {
      if (s >= targetStep) break;
      if (!canProceedFromStep(s)) return false;
    }
    return true;
  };

  const prevStep = () => {
    if (!isFirstStep) {
      setStep(visibleSteps[currentIdx - 1]);
      setIsEditMode(false);
    }
  };

  // ── Step status helpers ────────────────────────────────────────────────────
  const getStepStatus = (stepNum) =>
    stepStatus?.[`step${stepNum}`] || "pending";

  // A step is locked for saving if it's been submitted/approved and not in edit mode
  const isStepLocked = (stepNum) => {
    const s = getStepStatus(stepNum);
    if (isEditMode) return false;
    return s === "submitted" || s === "approved";
  };

  // ── Fetch permissions on mount ─────────────────────────────────────────────
  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await axios.get(
          `${configure.nodeApi}/rfp_permissions/${userId()}`,
          { headers: { Accept: "application/json" } },
        );
        if (res.data.success) {
          // API returns already-parsed perms object
          setMyPerms(res.data.data);
        }
      } catch (err) {
        console.error("fetchPerms:", err);
      }
    };
    fetchPerms();
  }, []);

  // ── Fetch service options ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchOptions = async () => {
      const res = await fetchNodeData(setLoading, "ztplPerformaIndex", token);
      if (res?.status === 200) setAllOption(res?.data);
    };
    fetchOptions();
  }, []);

  // ── Load existing RFP on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!leadId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${configure.nodeApi}/get_rfp_by_lead/${leadId}`,
          { headers: { Accept: "application/json" } },
        );
        if (res.data.success && res.data.data) {
          hydrateFromRfp(res.data.data);
        }
      } catch (err) {
        console.error("loadExistingRfp:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [leadId]);

  // ── Populate state from existing RFP ──────────────────────────────────────
  const hydrateFromRfp = (rfp) => {
    setRfpId(rfp.id);
    setRfpStatus(rfp.status);

    const ss = rfp.step_status || { step1: "pending", step2: "pending", step3: "pending", step4: "pending" };
    setStepStatus(ss);

    if (rfp.step_logs) setStepLogs(rfp.step_logs);
    if (rfp.services) setServices(rfp.services);
    if (rfp.pricing_mode) setPricingMode(rfp.pricing_mode);
    if (rfp.features) setFeatures(rfp.features);
    if (rfp.timing) setTiming(rfp.timing);

    if (rfp.currency) {
      setCostingCurrency({
        value: rfp.currency,
        label: rfp.currency,
        symbol: rfp.currency_symbol,
      });
    }

    // Restore 3-role technologies from roles_breakdown
    const roles = rfp.roles_breakdown;
    if (roles && typeof roles === "object") {
      if (roles.dev?.technology) setDevTechOption({ value: roles.dev.technology, label: roles.dev.technology, rateRow: null });
      if (roles.ba?.technology) setBaTechOption({ value: roles.ba.technology, label: roles.ba.technology, rateRow: null });
      if (roles.qa?.technology) setQaTechOption({ value: roles.qa.technology, label: roles.qa.technology, rateRow: null });
    }

    if (rfp.overhead_per_hr) setOverheadTotal(Number(rfp.overhead_per_hr));

    if (rfp.profit_pct) {
      setProfitPct(rfp.profit_pct || "");
    }

    if (rfp.commission) {
      setCommissionPct(rfp.commission.pct || "");
      if (rfp.commission.staff_id) {
        setCommissionStaff({ value: rfp.commission.staff_id, label: `Staff #${rfp.commission.staff_id}` });
      }
    }

    if (rfp.gocardless) {
      setGoCardlessEnabled(rfp.gocardless.enabled || false);
      setGoCardlessPct(rfp.gocardless.pct || "");
    }

    if (rfp.gst) setGstPct(rfp.gst.pct?.toString() || "18");

    if (rfp.base_cost !== undefined && rfp.base_cost !== null) {
      setSavedCosting({
        dev: roles?.dev || { technology: null, hours: 0, rate: 0, cost: 0 },
        ba: roles?.ba || { technology: null, hours: 0, rate: 0, cost: 0 },
        qa: roles?.qa || { technology: null, hours: 0, rate: 0, cost: 0 },
        overhead: roles?.overhead || { per_hr: 0, hours: 0, cost: 0 },
        total_hours: roles?.total_hours || (Number(rfp.timing_total_hrs) || 0),
        base_cost: rfp.base_cost,
        commission: rfp.commission,
        gocardless: rfp.gocardless,
        gst: rfp.gst,
        profit_pct: rfp.profit_pct,
        profit_amount: rfp.base_cost * ((Number(rfp.profit_pct) || 0) / 100),
        grand_total: rfp.grand_total,
        currency: rfp.currency,
        currency_symbol: rfp.currency_symbol,
      });
    }
  };

  useEffect(() => {
    const stepParam = searchParams.get("step");
    if (stepParam) {
      const stepNum = Number(stepParam);
      if ([1, 2, 3, 4, 5].includes(stepNum)) {
        setStep(stepNum);
      }
    }
  }, [searchParams]);

  // ── Service options ────────────────────────────────────────────────────────
  const serviceOptions =
    allOption?.service?.map((obj) => ({
      value: obj?.id,
      label: obj?.service,
      ...obj,
    })) || [];

  // ── Step 1 Save ────────────────────────────────────────────────────────────
  const saveStep1 = async () => {
    if (!services.length)
      return Swal.fire({
        icon: "warning",
        text: "Please select at least one service",
      });

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("lead_id", leadId);
      formData.append("created_by", userId());
      formData.append("pricing_mode", pricingMode);
      formData.append("services", JSON.stringify(services));

      const res = await axios.post(`${configure.nodeApi}/create_rfp`, formData);

      if (res.data.success) {
        setRfpId(res.data.data.id);
        setStepStatus(res.data.data.step_status);
        setRfpStatus(res.data.data.status);
        setIsEditMode(false);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: "success",
          text: "Services saved!",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("saveStep1:", err);
      Swal.fire({ icon: "error", text: "Failed to save Step 1" });
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 Save ────────────────────────────────────────────────────────────
  const saveStep2 = async () => {
    if (!rfpId)
      return Swal.fire({ icon: "warning", text: "Please save Step 1 first" });
    if (!features.length)
      return Swal.fire({
        icon: "warning",
        text: "Please add at least one feature",
      });

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("user_id", userId());
      formData.append("features", JSON.stringify(features));

      const res = await axios.post(
        `${configure.nodeApi}/save_rfp_step2`,
        formData,
      );

      if (res.data.success) {
        setStepStatus(res.data.data.step_status);
        setIsEditMode(false);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: "success",
          text: "Features saved!",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("saveStep2:", err);
      Swal.fire({ icon: "error", text: "Failed to save Step 2" });
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3 Save ────────────────────────────────────────────────────────────
  const saveStep3 = async () => {
    if (!rfpId)
      return Swal.fire({ icon: "warning", text: "Please save Step 1 first" });

    const devHrs = computeRoleHours(timing, features, "dev");
    const baHrs = computeRoleHours(timing, features, "ba");
    const qaHrs = computeRoleHours(timing, features, "qa");
    let totalHrs = devHrs + baHrs + qaHrs;

    if (totalHrs === 0) {
      return Swal.fire({
        icon: "warning",
        title: "No hours entered",
        text: "Please enter at least some hours before saving timing.",
        confirmButtonColor: "#752a97",
      });
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("user_id", userId());
      formData.append("timing", JSON.stringify(timing));
      formData.append("timing_dev_hrs", devHrs);
      formData.append("timing_ba_hrs", baHrs);
      formData.append("timing_qa_hrs", qaHrs);
      formData.append("timing_total_hrs", devHrs + baHrs + qaHrs);

      const res = await axios.post(
        `${configure.nodeApi}/save_rfp_step3`,
        formData,
      );

      if (res.data.success) {
        setStepStatus(res.data.data.step_status);
        setIsEditMode(false);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: "success",
          text: "Timing saved!",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("saveStep3:", err);
      Swal.fire({ icon: "error", text: "Failed to save Step 3" });
    } finally {
      setLoading(false);
    }
  };

  const saveStep4 = async () => {
    if (!rfpId)
      return Swal.fire({ icon: "warning", text: "Please save Step 1 first" });

    const curr = costingCurrency?.value || "GBP";
    const currSymbol = costingCurrency?.symbol || "£";
    const rateOf = (opt) => (opt?.rateRow ? Number(opt.rateRow[curr]) || 0 : 0);

    const devHrs = computeRoleHours(timing, features, "dev");
    const baHrs = computeRoleHours(timing, features, "ba");
    const qaHrs = computeRoleHours(timing, features, "qa");
    const totalHours = devHrs + baHrs + qaHrs;

    const devRate = rateOf(devTechOption);
    const baRate = rateOf(baTechOption);
    const qaRate = rateOf(qaTechOption);

    const devCost = devHrs * devRate;
    const baCost = baHrs * baRate;
    const qaCost = qaHrs * qaRate;
    const overheadPerHr = Number(overheadTotal) || 0;
    const overheadCost = totalHours * overheadPerHr;

    const baseCost = devCost + baCost + qaCost + overheadCost;
    const profitAmount = baseCost * ((Number(profitPct) || 0) / 100);
    const afterProfit = baseCost + profitAmount;
    const commissionAmount = afterProfit * ((Number(commissionPct) || 0) / 100);
    const afterCommission = afterProfit + commissionAmount;
    const goCardlessAmount = goCardlessEnabled
      ? afterCommission * ((Number(goCardlessPct) || 0) / 100)
      : 0;
    const subTotal = afterCommission + goCardlessAmount;
    const gstAmount = goCardlessEnabled
      ? subTotal * ((Number(gstPct) || 0) / 100)
      : 0;
    const grandTotal = subTotal + gstAmount;

    // 3-role breakdown — saved to roles_breakdown JSON column
    const rolesData = {
      dev: { technology: devTechOption?.value || null, hours: devHrs, rate: devRate, cost: devCost },
      ba: { technology: baTechOption?.value || null, hours: baHrs, rate: baRate, cost: baCost },
      qa: { technology: qaTechOption?.value || null, hours: qaHrs, rate: qaRate, cost: qaCost },
      overhead: { per_hr: overheadPerHr, hours: totalHours, cost: overheadCost },
      total_hours: totalHours,
    };

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("user_id", userId());
      formData.append("currency", curr);
      formData.append("currency_symbol", currSymbol);
      formData.append("technology", devTechOption?.value || "");
      formData.append("base_rate_per_hr", parseFloat(devRate.toFixed(2)));
      formData.append("overhead_per_hr", parseFloat(overheadPerHr.toFixed(2)));
      formData.append("final_rate_per_hr",
        parseFloat((totalHours > 0 ? baseCost / totalHours : 0).toFixed(2)));
      formData.append("base_cost", parseFloat(baseCost.toFixed(2)));
      formData.append("profit_pct", parseFloat(Number(profitPct || 0).toFixed(2)));
      formData.append("profit_amount", parseFloat(profitAmount.toFixed(2)));
      formData.append("commission_staff_id", commissionStaff?.value || "");
      formData.append("commission_pct", parseFloat(Number(commissionPct || 0).toFixed(2)));
      formData.append("commission_amount", parseFloat(commissionAmount.toFixed(2)));
      formData.append("gocardless_enabled", goCardlessEnabled);
      formData.append("gocardless_pct", parseFloat(Number(goCardlessPct || 0).toFixed(2)));
      formData.append("gocardless_amount", parseFloat(goCardlessAmount.toFixed(2)));
      formData.append("gst_pct", goCardlessEnabled ? parseFloat(Number(gstPct || 0).toFixed(2)) : 0);
      formData.append("gst_amount", parseFloat(gstAmount.toFixed(2)));
      formData.append("grand_total", parseFloat(grandTotal.toFixed(2)));
      formData.append("roles_data", JSON.stringify(rolesData));

      const res = await axios.post(`${configure.nodeApi}/save_rfp_step4`, formData);

      if (res.data.success) {
        setStepStatus(res.data.data.step_status);
        setIsEditMode(false);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        setSavedCosting({
          ...rolesData,
          base_cost: baseCost,
          profit_pct: profitPct,
          profit_amount: profitAmount,
          commission: { staff_id: commissionStaff?.value || null, pct: Number(commissionPct) || 0, amount: commissionAmount },
          gocardless: { enabled: goCardlessEnabled, pct: Number(goCardlessPct) || 0, amount: goCardlessAmount },
          gst: { pct: Number(gstPct) || 0, amount: gstAmount },
          grand_total: grandTotal,
          currency: curr,
          currency_symbol: currSymbol,
        });
        Swal.fire({ icon: "success", text: "Costing saved!", timer: 1200, showConfirmButton: false });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("saveStep4:", err);
      Swal.fire({ icon: "error", text: "Failed to save Step 4" });
    } finally {
      setLoading(false);
    }
  };

  // ── Submit step for approval ───────────────────────────────────────────────
  const submitStepForApproval = async (stepNum) => {
    if (!rfpId) return;

    const { isConfirmed } = await Swal.fire({
      title: `Submit Step ${stepNum} for Approval?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      confirmButtonColor: "#752a97",
    });
    if (!isConfirmed) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("step", stepNum);
      formData.append("user_id", userId());
      formData.append("status", "submitted");

      const res = await axios.post(
        `${configure.nodeApi}/update_rfp_step_status`,
        formData,
      );

      if (res.data.success) {
        setStepStatus(res.data.data.step_status);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: "success",
          text: `Step ${stepNum} submitted for approval!`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("submitStep:", err);
      Swal.fire({ icon: "error", text: "Failed to submit step" });
    } finally {
      setLoading(false);
    }
  };

  // ── Approve / Reject a step ────────────────────────────────────────────────
  const approveStep = async (stepNum, newStatus) => {
    if (!rfpId) return;

    let rejection_reason = "";

    if (newStatus === "rejected") {
      const { value } = await Swal.fire({
        title: "Reason for rejection",
        input: "textarea",
        inputPlaceholder: "Enter reason...",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#d33",
        inputValidator: (v) => !v.trim() && "Reason is required",
      });
      if (!value) return;
      rejection_reason = value;
    } else {
      const { isConfirmed } = await Swal.fire({
        title: `Approve Step ${stepNum}?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Approve",
        confirmButtonColor: "#752a97",
      });
      if (!isConfirmed) return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("step", stepNum);
      formData.append("user_id", userId());
      formData.append("status", newStatus);
      if (rejection_reason)
        formData.append("rejection_reason", rejection_reason);

      const res = await axios.post(
        `${configure.nodeApi}/update_rfp_step_status`,
        formData,
      );

      if (res.data.success) {
        setStepStatus(res.data.data.step_status);
        setRfpStatus(res.data.data.status);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: newStatus === "approved" ? "success" : "info",
          text: `Step ${stepNum} ${newStatus}`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("approveStep:", err);
      Swal.fire({ icon: "error", text: "Failed to update step status" });
    } finally {
      setLoading(false);
    }
  };

  // ── Final RFP approve / reject (step 5) ───────────────────────────────────
  const updateFinalStatus = async (newStatus) => {
    if (!rfpId) return;

    let rejection_reason = "";

    if (newStatus === "rejected") {
      const { value } = await Swal.fire({
        title: "Reason for rejection",
        input: "textarea",
        inputPlaceholder: "Enter reason...",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#d33",
        inputValidator: (v) => !v.trim() && "Reason is required",
      });
      if (!value) return;
      rejection_reason = value;
    } else {
      const { isConfirmed } = await Swal.fire({
        title: "Final Approval — Approve this RFP?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Approve",
        confirmButtonColor: "#752a97",
      });
      if (!isConfirmed) return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("id", rfpId);
      formData.append("status", newStatus);
      formData.append("updated_by", userId());
      if (rejection_reason)
        formData.append("rejection_reason", rejection_reason);

      const res = await axios.post(
        `${configure.nodeApi}/update_rfp_status`,
        formData,
      );

      if (res.data.success) {
        setRfpStatus(res.data.data.status);
        if (res.data.data.step_logs) setStepLogs(res.data.data.step_logs);
        Swal.fire({
          icon: newStatus === "approved" ? "success" : "info",
          text: `RFP ${newStatus} successfully`,
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", text: res.data.message });
      }
    } catch (err) {
      console.error("updateFinalStatus:", err);
      Swal.fire({ icon: "error", text: "Failed to update RFP status" });
    } finally {
      setLoading(false);
    }
  };

  // ── Save dispatcher ────────────────────────────────────────────────────────
  const saveFns = { 1: saveStep1, 2: saveStep2, 3: saveStep3, 4: saveStep4 };
  const handleSave = () => saveFns[step]?.();

  // Step 1 mirrors step 2's approval status visually
  const getDisplayStatus = (stepNum) => {
    if (stepNum === 1) {
      const s2 = getStepStatus(2);
      if (s2 === "approved") return "approved";
      if (s2 === "submitted") return "submitted";
      return getStepStatus(1); // "done" or "pending"
    }

    if (stepNum === 4) {
      if (rfpStatus === "approved") return "approved";
      return getStepStatus(4);
    }

    if (stepNum === 5) {
      if (rfpStatus === "approved") return "approved";
      return "pending"; // step 5 has no intermediate status
    }
    return getStepStatus(stepNum);
  };

  // ── Sidebar step description ───────────────────────────────────────────────
  const stepDesc = (stepNum) => {
    const status = getDisplayStatus(stepNum);
    if (status === "approved") return "Approved";
    if (status === "submitted") return "Awaiting Approval";
    if (status === "rejected") return "✗ Rejected";
    if (status === "done") return "Completed";
    return STEP_DESC_DEFAULT[stepNum];
  };

  // ── No access guard ────────────────────────────────────────────────────────
  if (visibleSteps.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Dashboard"
            breadcrumbItem="Request For Proposal"
          />
          <div className="text-center mt-5">
            <h4 className="text-muted">You don't have access to this RFP.</h4>
          </div>
        </Container>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {isLoading && <Loader />}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Dashboard"
            breadcrumbItem="Request For Proposal"
          />

          {/* ── RFP status banner ── */}
          {rfpStatus && (
            <div
              className={`rfp-status-banner rfp-status-banner--${rfpStatus}`}
            >
              <span>
                RFP Status:{" "}
                <strong style={{ textTransform: "capitalize" }}>
                  {rfpStatus}
                </strong>
              </span>
            </div>
          )}

          <Row className="rfp-wizard-ui request-for-proposal">
            {/* ── SIDEBAR ── */}
            <Col lg={2} className="rfp-sidebar">
              <div className="rfp-steps">
                {visibleSteps.map((stepNum) => (
                  <WizardStep
                    key={stepNum}
                    stepNum={stepNum}
                    currentStep={step}
                    title={STEP_TITLE[stepNum]}
                    desc={stepDesc(stepNum)}
                    status={getDisplayStatus(stepNum)}
                    onClick={() => {
                      if (!canNavigateToStep(stepNum)) {
                        Swal.fire({
                          icon: "warning",
                          text: "Please complete and save/get approval on the previous steps first.",
                          confirmButtonColor: "#752a97",
                        });
                        return;
                      }
                      setStep(stepNum);
                      setIsEditMode(false);
                    }}
                  />
                ))}
              </div>
            </Col>

            {/* ── CONTENT ── */}
            <Col lg={10} className="rfp-content">
              <div className="rfp-card">
                {step === 1 && (
                  <StepServices
                    services={services}
                    setServices={setServices}
                    serviceOptions={serviceOptions}
                    pricingMode={pricingMode}
                    setPricingMode={setPricingMode}
                    stepLogs={stepLogs?.step1}
                    readOnly={
                      isReadOnly(1) ||
                      (!!rfpId &&
                        !isEditMode &&
                        (getStepStatus(1) === "done" || isUnlocked))
                    }
                    allUsers={allUsers}
                  />
                )}

                {step === 2 && (
                  <StepFeatures
                    features={features}
                    setFeatures={setFeatures}
                    services={services}
                    stepLogs={stepLogs}
                    // readOnly={isReadOnly(2) || isStepLocked(2)}
                    readOnly={
                      isReadOnly(2) ||
                      (!!rfpId &&
                        !isEditMode &&
                        (isStepLocked(2) ||
                          getStepStatus(2) === "done" ||
                          isUnlocked))
                    }
                    allUsers={allUsers}
                  />
                )}

                {step === 3 && (
                  <StepTiming
                    features={features}
                    timing={timing}
                    setTiming={setTiming}
                    services={services}
                    pricingMode={pricingMode}
                    extraHours={extraHours}
                    setExtraHours={setExtraHours}
                    stepLogs={stepLogs}
                    readOnly={
                      isReadOnly(3) ||
                      (!!rfpId &&
                        !isEditMode &&
                        (isStepLocked(3) ||
                          getStepStatus(3) === "done" ||
                          isUnlocked))
                    }
                    allUsers={allUsers}
                  />
                )}


                {step === 4 && (
                  <StepCosting
                    features={features}
                    timing={timing}
                    services={services}
                    pricingMode={pricingMode}
                    costingCurrency={costingCurrency}
                    setCostingCurrency={setCostingCurrency}
                    devTechOption={devTechOption} setDevTechOption={setDevTechOption}
                    baTechOption={baTechOption} setBaTechOption={setBaTechOption}
                    qaTechOption={qaTechOption} setQaTechOption={setQaTechOption}
                    overheadTotal={overheadTotal} setOverheadTotal={setOverheadTotal}
                    commissionStaff={commissionStaff} setCommissionStaff={setCommissionStaff}
                    commissionPct={commissionPct} setCommissionPct={setCommissionPct}
                    profitPct={profitPct} setProfitPct={setProfitPct}
                    goCardlessEnabled={goCardlessEnabled} setGoCardlessEnabled={setGoCardlessEnabled}
                    goCardlessPct={goCardlessPct} setGoCardlessPct={setGoCardlessPct}
                    gstPct={gstPct} setGstPct={setGstPct}
                    stepLogs={stepLogs}
                    readOnly={isReadOnly(4) || (!!rfpId && !isEditMode && (getStepStatus(4) === "done" || isUnlocked))}
                    allUsers={allUsers}
                  />
                )}
                {step === 5 && (
                  <StepOverview
                    services={services}
                    features={features}
                    timing={timing}
                    pricingMode={pricingMode}
                    savedCosting={savedCosting}
                    stepLogs={stepLogs}
                    allUsers={allUsers}
                  />
                )}
              </div>

              {/* ── ACTIONS BAR ── */}
              <div className="rfp-actions">
                {/* Previous */}
                {!isFirstStep && (
                  <button className="btn btn-secondary" onClick={prevStep}>
                    ← Previous
                  </button>
                )}

                <div className="d-flex gap-2 ms-auto align-items-center">
                  {/* Step status badge */}
                  {step !== 5 && getStepStatus(step) !== "pending" && (
                    <span
                      className={`rfp-step-status-badge rfp-step-status-badge--${getStepStatus(
                        step,
                      )}`}
                    >
                      {getStepStatus(step) === "submitted" &&
                        "⌛︎ Awaiting Approval"}
                      {getStepStatus(step) === "approved" && "✓ Approved"}
                      {getStepStatus(step) === "rejected" && "✗ Rejected"}
                    </span>
                  )}

                  {/* Edit toggle — step is done/locked and user has edit permission */}
                  {step !== 5 &&
                    !isApproved &&
                    rfpId &&
                    (isUnlocked ||
                      isStepLocked(step) ||
                      getStepStatus(step) === "done") &&
                    p(step).edit == 1 && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setIsEditMode((prev) => !prev)}
                      >
                        {isEditMode ? (
                          <>
                            <i className="dripicons-cross me-1 icon-center"></i>
                            Cancel Edit
                          </>
                        ) : (
                          <>
                            <i className="fas fa-pencil-alt me-1"></i>Edit
                          </>
                        )}
                      </button>
                    )}

                  {/* Save — has add/edit permission and step not locked */}
                  {step !== 5 &&
                    canSave(step) &&
                    (isEditMode ||
                      (!isStepLocked(step) &&
                        !isUnlocked &&
                        getStepStatus(step) !== "done")) && (
                      <button
                        className="btn btn-save btn-label"
                        onClick={handleSave}
                      >
                        <i className="fas fa-save me-2 label-icon"></i>
                        Save Step {step}
                      </button>
                    )}

                  {/* Submit for approval — only on step 2 and step 3 */}
                  {(step === 2 || step === 3) &&
                    canSubmit(step) &&
                    getStepStatus(step) === "done" &&
                    !isEditMode && (
                      <button
                        className="btn btn-purple btn-label"
                        onClick={() => submitStepForApproval(step)}
                      >
                        <i className="fas fa-paper-plane me-2 label-icon"></i>
                        Submit for Approval
                      </button>
                    )}

                  {/* Approve / Reject step — has approve permission, step is submitted */}
                  {(step === 2 || step === 3) &&
                    canApprove(step) &&
                    getStepStatus(step) === "submitted" && (
                      <>
                        <button
                          className="btn btn-success"
                          onClick={() => approveStep(step, "approved")}
                        >
                          ✓ Approve Step {step}
                        </button>
                      </>
                    )}

                  {step === 5 && canApprove(5) && rfpStatus === "pending" && (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={() => {
                          const step2Status = getStepStatus(2);
                          const step3Status = getStepStatus(3);
                          const step4Status = getStepStatus(4);
                          const notApproved = [];
                          if (step2Status !== "approved")
                            notApproved.push("Step 2 (Features)");
                          if (step3Status !== "approved")
                            notApproved.push("Step 3 (Timing)");
                          if (step4Status !== "done")
                            notApproved.push(
                              "Costing (Step 4) — must be saved",
                            );

                          if (notApproved.length > 0) {
                            Swal.fire({
                              icon: "warning",
                              title: "Approval Required",
                              html: `The following steps must be approved before final approval:<br/><br/><strong>${notApproved.join(
                                "<br/>",
                              )}</strong>`,
                              confirmButtonText: "OK",
                              confirmButtonColor: "#752a97",
                            });
                            return;
                          }

                          updateFinalStatus("approved");
                        }}
                      >
                        ✓ Final Approve
                      </button>
                    </>
                  )}

                  {/* Unlock — only after RFP is fully approved */}
                  {/* Unlock Edit — only for final approval level users, after final approval */}
                  {step === 5 && canApprove(5) && isFinallyApproved && (
                    <button
                      className="btn btn-danger btn-label"
                      onClick={async () => {
                        const { isConfirmed } = await Swal.fire({
                          icon: "warning",
                          title: "Unlock for Editing?",
                          text: "This will allow changes to all steps. The RFP status will be reset to pending.",
                          showCancelButton: true,
                          confirmButtonText: "Yes, Unlock",
                          confirmButtonColor: "#752a97",
                        });
                        if (!isConfirmed) return;

                        setLoading(true);
                        try {
                          const formData = new FormData();
                          formData.append("id", rfpId);
                          formData.append("status", "pending");
                          formData.append("updated_by", userId());

                          const res = await axios.post(
                            `${configure.nodeApi}/update_rfp_status`,
                            formData,
                          );
                          if (res.data.success) {
                            setRfpStatus("pending");
                            setIsUnlocked(true);
                            // Only reset step5 logs locally — steps 1-4 keep their real status
                            setStepLogs((prev) => ({
                              ...prev,
                              step5: { approved: [] },
                            }));
                          } else {
                            Swal.fire({
                              icon: "error",
                              text: res.data.message,
                            });
                          }
                        } catch (err) {
                          console.error("unlock:", err);
                          Swal.fire({
                            icon: "error",
                            text: "Failed to unlock RFP",
                          });
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      <i className="fas fa-unlock me-2 label-icon"></i>
                      {isUnlocked ? "Editing Unlocked" : "Unlock Edit"}
                    </button>
                  )}

                  {/* Create Proposal — only after final approval, only Level 3 */}
                  {step === 5 && (Number(stepLogs?.step1?.saved?.[0]?.by) === userId()) && isFinallyApproved && (
                    <button
                      className="btn btn-purple btn-label"
                      onClick={async () => {
                        const { isConfirmed } = await Swal.fire({
                          title: "Create Proposal?",
                          text: "This will convert the RFP into a proposal.",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonText: "Yes, Create",
                          confirmButtonColor: "#28a745",
                        });
                        if (!isConfirmed) return;

                        setLoading(true);

                        try {
                          const formData = new FormData();
                          formData.append("lead_id", leadId);
                          formData.append("user_id", userId());
                          formData.append("rfp_id", rfpId);

                          const res = await axios.post(
                            `${configure.nodeApi}/convert_rfp_to_proposal`,
                            formData,
                            { headers: { Accept: "application/json" } },
                          );

                          const proposalId = res.data?.data?.id;

                          setLoading(false);

                          // if (res.data.success && proposalId) {

                          //   Swal.fire({
                          //     icon: "success",
                          //     text: res.data.already_converted
                          //       ? "Proposal already exists — opening it."
                          //       : "Proposal created successfully!",
                          //     timer: 1500,
                          //     showConfirmButton: false,
                          //   });

                          //   window.open(
                          //     `${configure.appUrl}sales/edit-proposal/${proposalId}`,
                          //     "_blank",
                          //   );
                          // } 
                          if (res.data.success && proposalId) {
                            setLoading(false);

                            // ✅ Open first, then show success toast — don't await between open and the call
                            const newTab = window.open(
                              `${configure.appUrl}sales/edit-proposal/${proposalId}`,
                              "_blank",
                            );

                            // If popup was blocked, fallback
                            if (!newTab) {
                              Swal.fire({
                                icon: "success",
                                title: "Proposal Created!",
                                // text: "Popup was blocked. Click below to open.",
                                confirmButtonText: "Open Proposal",
                                confirmButtonColor: "#28a745",
                              }).then(() => {
                                window.location.href = `${configure.appUrl}sales/edit-proposal/${proposalId}`;
                              });
                            } else {
                              // Show a non-blocking toast
                              Swal.fire({
                                icon: "success",
                                text: "Proposal created successfully!",
                                timer: 1500,
                                showConfirmButton: false,
                              });
                            }
                          } else {
                            Swal.fire({ icon: "error", text: res.data.message || "Failed to create proposal" });
                          }
                        }
                        catch (err) {
                          console.error("createProposal:", err);
                          setLoading(false)
                          Swal.fire({ icon: "error", text: "Failed to create proposal" });

                        }
                        // } finally {
                        //   setLoading(false); 
                        // }
                      }}
                    >
                      <i className="fas fa-file-alt me-2 label-icon"></i>
                      Create Proposal
                    </button>
                  )}

                  {/* Next */}
                  {!isLastStep && (
                    <button
                      className="btn btn-primary rfp-next-btn"
                      onClick={nextStep}
                      disabled={!canProceedFromStep(step)}
                      title={
                        !canProceedFromStep(step)
                          ? "Complete this step first"
                          : ""
                      }
                    >
                      Continue →
                    </button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const WizardStep = ({ stepNum, currentStep, title, desc, status, onClick }) => {
  const isActive = currentStep === stepNum;
  const isApproved = status === "approved";
  const isSubmitted = status === "submitted";
  const isRejected = status === "rejected";
  const isDone = status === "done";

  const circleContent = () => {
    if (isApproved) return "✓";
    if (isRejected) return "✗";
    if (isSubmitted) return "⌛︎";
    if (isDone) return "✓";
    return stepNum;
  };

  return (
    <div
      className={[
        "rfp-step",
        isActive ? "rfp-active" : "",
        isApproved ? "rfp-approved" : "",
        isSubmitted ? "rfp-submitted" : "",
        isRejected ? "rfp-rejected" : "",
        isDone ? "rfp-done" : "",
      ].join(" ")}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <div className="rfp-step-circle">{circleContent()}</div>
      <div className="rfp-step-text">
        <div className="rfp-step-title">{title}</div>
        <div className="rfp-step-desc">{desc}</div>
      </div>
    </div>
  );
};

export default RequestForProposal;